import React from 'react';
import Card, { CardProps } from '@material-ui/core/Card';
import CardMedia, { CardMediaProps } from '@material-ui/core/CardMedia';
import CardContent, { CardContentProps } from '@material-ui/core/CardContent';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography, { TypographyProps } from '@material-ui/core/Typography';
import cx from 'clsx';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      cursor: 'pointer',
      display: 'flex',
      [theme.breakpoints.up('md')]: {
        flex: '0 0 30%'
      },
      [theme.breakpoints.up('sm')]: {
        flex: '0 0 50%'
      },
      [theme.breakpoints.up('xs')]: {
        flex: '0 0 100%'
      }
    },
    card: {
      maxWidth: theme.typography.pxToRem(360),
      margin: '0 auto',
      height: '100%',
      '&:hover': {
        boxShadow: '0 1px 8px 4px rgba(0, 0, 0, 0.20)',
        transform: 'scale(1.01, 1.01)'
      }
    },
    media: {
      height: theme.typography.pxToRem(190),
      maxWidth: '100%',
      marginBottom: theme.spacing(0.5),
      backgroundImage: 'linear-gradient(180deg, #FFFFFF 0%, #EEEEEE 100%)'
    },
    bottomSpacing: {
      marginBottom: theme.typography.pxToRem(12)
    },
    title: {
      height: theme.typography.pxToRem(35)
    },
    content: {
      paddingLeft: theme.typography.pxToRem(28),
      paddingRight: theme.typography.pxToRem(28),
      paddingBottom: theme.spacing(3)
    },
    grey: { color: '#696969' }
  };
});

export interface AppCardProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  title?: string;
  subtitle?: string;
  description?: string | null;
  titleProps?: TypographyProps;
  subtitleProps?: TypographyProps;
  descriptionProps?: TypographyProps;
  mediaProps?: CardMediaProps;
  contentContainerProps?: CardContentProps;
  cardProps?: CardProps;
  src?: string;
  href?: string;
  openNew?: boolean;
}

const AppCard: React.FC<AppCardProps> = ({
  title,
  subtitle,
  description,
  src,
  titleProps,
  subtitleProps,
  descriptionProps,
  cardProps,
  mediaProps,
  contentContainerProps,
  openNew,
  href,
  ...rest
}) => {
  const classes = useStyles();
  const history = useHistory();

  const navigate = React.useMemo(() => {
    if (href) {
      if (openNew) {
        return (): void => {
          window.open(href, '_newtab');
        };
      }
      return (): void => {
        history.push(href);
      };
    }
    return undefined;
  }, [href, history, openNew]);

  return (
    <div className={classes.root} {...rest}>
      <Card className={classes.card} onClick={navigate} {...cardProps}>
        {src && <CardMedia className={classes.media} image={src} title={title} {...mediaProps} />}
        <CardContent className={classes.content} {...contentContainerProps}>
          {title && (
            <Typography
              className={classes.bottomSpacing}
              align="center"
              variant="h3"
              {...titleProps}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              className={cx(classes.bottomSpacing, classes.grey)}
              align="center"
              variant="subtitle2"
              {...subtitleProps}
            >
              {subtitle}
            </Typography>
          )}
          {description && (
            <Typography variant="body1" {...descriptionProps}>
              {description}
            </Typography>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AppCard;
